<template>
  <v-container grid-list-xl fluid>
    <v-layout row wrap>
      <v-flex lg12>
        <v-card>
          <!-- Filter section -->
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="startDate"
                      :label="$t('customer_debt.from_date')"
                      prepend-icon="event"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="startDate"
                    @input="startDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              
              <v-col cols="12" md="4">
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="endDate"
                      :label="$t('customer_debt.to_date')"
                      prepend-icon="event"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="endDate"
                    @input="endDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="customerName"
                  :label="$t('customer_debt.customer_name')"
                  prepend-icon="person"
                  clearable
                ></v-text-field>
              </v-col>
            </v-row>
            
            <v-row>
              <v-col cols="12" class="text-right">
                <v-btn color="primary" @click="filterData" :loading="loading">
                  <v-icon left>search</v-icon>
                  {{ $t('customer_debt.search') }}
                </v-btn>
                <v-btn color="success" @click="exportExcel" :loading="loading" class="ml-2">
                  <v-icon left>get_app</v-icon>
                  {{ $t('customer_debt.export_excel') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
          
          <!-- Summary cards -->
          <v-card-text v-if="totals && totals.TotalCustomers">
            <v-row>
              <v-col cols="12" md="3">
                <v-card color="primary" dark>
                  <v-card-text>
                    <div class="text-h6">{{ $t('customer_debt.total_customers') }}</div>
                    <div class="text-h4">{{ totals.TotalCustomers | currency }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col cols="12" md="3">
                <v-card color="info" dark>
                  <v-card-text>
                    <div class="text-h6">{{ $t('customer_debt.total_bills') }}</div>
                    <div class="text-h4">{{ totals.TotalBills | currency }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col cols="12" md="3">
                <v-card color="success" dark>
                  <v-card-text>
                    <div class="text-h6">{{ $t('customer_debt.total_amount') }}</div>
                    <div class="text-h4">{{ totals.GrandTotalAmount | currency }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col cols="12" md="3">
                <v-card color="error" dark>
                  <v-card-text>
                    <div class="text-h6">{{ $t('customer_debt.total_debt') }}</div>
                    <div class="text-h4">{{ totals.GrandTotalDebt | currency }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
          
          <!-- Data table -->
          <v-data-table
            :headers="headers"
            :items="items"
            :options.sync="pagination"
            :server-items-length="total_rows"
            :footer-props="items_per_page"
            :loading="loading"
            :mobile-breakpoint="100"
            class="elevation-1"
            fixed-header
            height="calc(100vh - 350px)"
          >
            <template v-slot:item.CustomerName="{ item }">
              <strong>{{ item.CustomerName }}</strong>
            </template>
            
            <template v-slot:item.TotalCodes="{ item }">
              <span>{{ item.TotalCodes | currency }}</span>
            </template>
            
            <template v-slot:item.TotalPacks="{ item }">
              <span>{{ item.TotalPacks | currency }}</span>
            </template>
            
            <template v-slot:item.TotalWeight="{ item }">
              <span>{{ item.TotalWeight | currency('', 1) }}</span>
            </template>
            
            <template v-slot:item.TotalVolume="{ item }">
              <span>{{ item.TotalVolume | currency('', 2) }}</span>
            </template>
            
            <template v-slot:item.TotalAmount="{ item }">
              <span>{{ item.TotalAmount | currency }}</span>
            </template>
            
            <template v-slot:item.TotalPaid="{ item }">
              <span class="green--text">{{ item.TotalPaid | currency }}</span>
            </template>
            
            <template v-slot:item.TotalDebt="{ item }">
              <span class="red--text font-weight-bold">{{ item.TotalDebt | currency }}</span>
            </template>
            
            <template v-slot:item.TotalBills="{ item }">
              <v-chip small color="primary">{{ item.TotalBills }}</v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import configs from "@/configs";
import moment from "moment";

export default {
  data() {
    return {
      startDate: moment().startOf('year').format('YYYY-MM-DD'),
      endDate: moment().endOf('year').format('YYYY-MM-DD'),
      startDateMenu: false,
      endDateMenu: false,
      customerName: "",
      pagination: {...configs.PAGINATION},
      items_per_page: {...configs.ITEMS_PER_PAGE},
    };
  },
  watch: {
    pagination: {
      handler: function() {
        this.filterData();
      },
      deep: true
    }
  },
  computed: {
    ...mapState({
      items: state => state.customerDebt.all.data,
      total_rows: state => state.customerDebt.all.total,
      totals: state => state.customerDebt.all.totals,
      loading: state => state.customerDebt.loading
    }),
    headers() {
      return [
        {
          text: this.$t('customer_debt.headers.customer_code'),
          value: "CustomerID",
          align: "start"
        },
        {
          text: this.$t('customer_debt.headers.customer_name'),
          value: "CustomerName",
          align: "start"
        },
        {
          text: this.$t('customer_debt.headers.bill_count'),
          value: "TotalBills",
          align: "center"
        },
        {
          text: this.$t('customer_debt.headers.total_codes'),
          value: "TotalCodes",
          align: "end"
        },
        {
          text: this.$t('customer_debt.headers.total_packs'),
          value: "TotalPacks",
          align: "end"
        },
        {
          text: this.$t('customer_debt.headers.total_weight'),
          value: "TotalWeight",
          align: "end"
        },
        {
          text: this.$t('customer_debt.headers.total_volume'),
          value: "TotalVolume",
          align: "end"
        },
        {
          text: this.$t('customer_debt.headers.total_amount'),
          value: "TotalAmount",
          align: "end"
        },
        {
          text: this.$t('customer_debt.headers.total_paid'),
          value: "TotalPaid",
          align: "end"
        },
        {
          text: this.$t('customer_debt.headers.total_debt'),
          value: "TotalDebt",
          align: "end"
        }
      ];
    }
  },
  methods: {
    filterData() {
      const param = {
        pagination: this.pagination,
        startTime: this.startDate + ' 00:00:00',
        endTime: this.endDate + ' 23:59:59',
        customerName: this.customerName || null
      };
      this.$store.dispatch("customerDebt/getList", param);
    },
    
    exportExcel() {
      const param = {
        startTime: this.startDate + ' 00:00:00',
        endTime: this.endDate + ' 23:59:59',
        customerName: this.customerName || null
      };
      this.$store.dispatch("customerDebt/exportList", param);
    }
  },
  mounted() {
    this.filterData();
  }
};
</script>

<style scoped>
.text-h4 {
  margin-top: 8px;
}
</style>
